import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { signIn, signInWithGoogle } from "../../redux/actions/authActions";
import SignInLayout from "../../layouts/SignInLayout";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import { persistPendingSocialRegistration } from "../../utils/socialAuth";

import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { spacing } from "@material-ui/system";
import { Alert as MuiAlert } from "@material-ui/lab";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../utils/apiError";
const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Wrapper = styled(Paper)`
  width: 100%;
  padding: ${(props) => props.theme.spacing(4)}px;
  border-radius: 30px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 28px 64px rgba(15, 23, 42, 0.12);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(5)}px;
  }
`;

const BigAvatar = styled(Avatar)`
  width: 84px;
  height: 84px;
  text-align: center;
  margin: 0;
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.18);
`;

const Hero = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef4ff 45%, #f8fafc 100%);
  border: 1px solid rgba(37, 99, 235, 0.1);
  margin-bottom: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 110px minmax(0, 1fr);
    align-items: center;
  }
`;

const HeroCopy = styled(Box)`
  min-width: 0;
`;

const Eyebrow = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled(Typography)`
  margin-top: 14px;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 800;
  line-height: 1.06;
  color: #0f172a;
`;

const HeroDescription = styled(Typography)`
  margin-top: 10px;
  color: #64748b;
  line-height: 1.75;
`;

const FormCard = styled(Box)`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
`;

const SectionEyebrow = styled(Typography)`
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2563eb;
  font-weight: 700;
`;

const SectionTitle = styled(Typography)`
  margin-top: 6px;
  font-size: 1.3rem;
  font-weight: 800;
  color: #0f172a;
`;

const SectionDescription = styled(Typography)`
  margin-top: 6px;
  color: #64748b;
  line-height: 1.6;
`;

const OptionsRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const RememberWrap = styled(Box)`
  padding: 10px 12px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const InlineLink = styled(Link)`
  color: #2563eb;
  font-weight: 700;
  text-decoration: none;
`;

const SubmitButton = styled(Button)`
  min-height: 52px;
  margin-top: 18px;
  border-radius: 18px;
  text-transform: none;
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.26);

  &:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  }
`;

const SecondaryAction = styled(Button)`
  min-height: 48px;
  border-radius: 16px;
  text-transform: none;
  font-weight: 700;
`;

const LinkGrid = styled(Grid)`
  margin-top: 12px;
`;

function SignIn() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation(); // Initialize the translation hook
  const [showPassword, setShowPassword] = React.useState(false);
  const [socialError, setSocialError] = React.useState("");
  const handleGoogleCredential = async (credential) => {
    setSocialError("");
    try {
      const response = await dispatch(signInWithGoogle(credential));

      if (response?.status === "LOGIN_SUCCESS" && response?.token) {
        history.push("/dashboard");
        return;
      }

      if (response?.status === "REGISTRATION_REQUIRED") {
        persistPendingSocialRegistration({
          credential,
          profile: response.profile,
        });
        history.push("/auth/social/complete-registration");
        return;
      }

      throw new Error(
        response?.message ||
          "No fue posible completar la autenticación con Google."
      );
    } catch (error) {
      setSocialError(
        error?.response?.data?.message ||
          error?.message ||
          "No fue posible completar la autenticación con Google."
      );
    }
  };

  return (
    <SignInLayout>
      <Wrapper>
        <Helmet title="Sign In" />
        <Hero>
          <BigAvatar alt="Wifiads" src="/static/img/avatars/logo.png" />
          <HeroCopy>
            <Eyebrow>{t("Client Access")}</Eyebrow>
            <HeroTitle>{t("Sistema de Clientes - Publicidades")}</HeroTitle>
            <HeroDescription>
              {t(
                "Ingrese a su cuenta para gestionar campañas, revisar resultados y continuar con el flujo comercial desde un solo lugar."
              )}
            </HeroDescription>
          </HeroCopy>
        </Hero>

        <Formik
          initialValues={{
            email: "",
            password: "",
            submit: false,
            remember: false,
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email(t("Introduzca un correo electrónico válido"))
              .max(255)
              .required(t("El correo electrónico es obligatorio")),
            password: Yup.string()
              .max(255)
              .required(t("La contraseña es obligatoria")),
          })}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
              await dispatch(
                signIn({
                  email: values.email,
                  password: values.password,
                  remember: values.remember,
                })
              );
              history.push("/dashboard");
            } catch (error) {
              const message = getApiErrorMessage(error);

              setStatus({ success: false });
              setErrors({ submit: message });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <FormCard>
                <SectionEyebrow>{t("Inicio de sesión")}</SectionEyebrow>
                <SectionTitle>{t("Acceda a su cuenta")}</SectionTitle>
                <SectionDescription>
                  {t(
                    "Utilice sus credenciales para entrar al panel y continuar con la administración de sus publicidades."
                  )}
                </SectionDescription>

                {errors.submit && (
                  <Alert mt={2} mb={2} severity="error" role="alert">
                    {errors.submit}
                  </Alert>
                )}

                {socialError && (
                  <Alert mt={2} mb={2} severity="error" role="alert">
                    {socialError}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      type="email"
                      name="email"
                      label={t("Correo electrónico")}
                      value={values.email}
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      autoComplete="email"
                      inputProps={{ "aria-label": t("Correo electrónico") }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      type={showPassword ? "text" : "password"}
                      name="password"
                      label={t("Contraseña")}
                      value={values.password}
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      my={0}
                      variant="outlined"
                      autoComplete="current-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword
                                  ? t("Ocultar contraseña")
                                  : t("Mostrar contraseña")
                              }
                              onClick={() =>
                                setShowPassword((visible) => !visible)
                              }
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <OptionsRow>
                  <RememberWrap>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="remember"
                          checked={values.remember}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label={t("Recordarme")}
                    />
                  </RememberWrap>
                  <InlineLink to="/auth/reset-password">
                    {t("¿Olvidó su contraseña?")}
                  </InlineLink>
                </OptionsRow>

                <SubmitButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <React.Fragment>
                      <CircularProgress
                        size={20}
                        color="inherit"
                        style={{ marginRight: 10 }}
                      />
                      {t("Ingresando...")}
                    </React.Fragment>
                  ) : (
                    t("Iniciar sesión")
                  )}
                </SubmitButton>

                <GoogleSignInButton
                  onCredential={handleGoogleCredential}
                  text="signin_with"
                  title={t("O continúe con Google")}
                />

                <LinkGrid container spacing={2}>
                  <Grid item xs={12}>
                    <Link to="/auth/sign-up" style={{ textDecoration: "none" }}>
                      <SecondaryAction color="secondary" fullWidth>
                        {t("Crear una cuenta")}
                      </SecondaryAction>
                    </Link>
                  </Grid>
                </LinkGrid>
              </FormCard>
            </form>
          )}
        </Formik>
      </Wrapper>
    </SignInLayout>
  );
}

export default SignIn;
